import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, ArrowLeft, AlertCircle, CheckCircle2, LogIn, Download, Menu, Inbox, LogOut, Shield, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { ContentTypeSelector, type ContentType } from '@/components/ContentTypeSelector';
import { MediaUploader } from '@/components/MediaUploader';
import { VoiceRecorder } from '@/components/VoiceRecorder';
import { NamePrompt } from '@/components/NamePrompt';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { useShareIntent } from '@/hooks/useShareIntent';
import { cn } from '@/lib/utils';
import { isCapacitorNative, signInWithGoogleNative, initializeGoogleAuth, getApiBaseUrl, finishShareIntent } from '@/lib/capacitor';
import { initializePushNotifications, unregisterPushToken } from '@/lib/pushNotifications';

type UploadState = 'idle' | 'uploading' | 'success' | 'processing' | 'error';

interface UploadResult {
  state: UploadState;
  progress: number;
  error?: string;
  postId?: string; // Track the post ID for navigation
}

export default function SharePage() {
  const navigate = useNavigate();
  const { 
    user, 
    isLoading: authLoading, 
    requiresGoogleAuth,
    loginLocal,
    getUserIdentifier,
    authenticatedFetch,
    logout,
  } = useAuth();
  
  const [contentType, setContentType] = useState<ContentType | null>(null);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const [caption, setCaption] = useState('');
  const [hudText, setHudText] = useState('');
  const [uploadState, setUploadState] = useState<UploadResult>({
    state: 'idle',
    progress: 0,
  });
  
  // Logout confirmation dialog state
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  
  // Native sign-in state
  const [isNative] = useState(() => isCapacitorNative());
  const [signInLoading, setSignInLoading] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);

  // Share intent handling (for when user shares from Android gallery)
  const { sharedMedia, isProcessing: isProcessingShareIntent, clearSharedMedia } = useShareIntent();
  
  // Track if this upload was triggered by a share intent (to close app after success)
  const [wasFromShareIntent, setWasFromShareIntent] = useState(false);

  // App download setting
  const [appDownloadEnabled, setAppDownloadEnabled] = useState(false);

  // Fetch settings to check if app download is enabled
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/settings');
        const data = await response.json();
        if (data.success) {
          setAppDownloadEnabled(data.settings.appDownloadEnabled);
        }
      } catch {
        // Silently fail - button just won't show
      }
    };
    fetchSettings();
  }, []);

  // Initialize native Google Auth
  useEffect(() => {
    if (isNative && requiresGoogleAuth) {
      initializeGoogleAuth();
    }
  }, [isNative, requiresGoogleAuth]);

  // Initialize push notifications when user is authenticated on native
  useEffect(() => {
    if (isNative && user?.id) {
      console.log('[SharePage] Initializing push notifications for user:', user.id);
      
      // Handle notification tap - navigate to the relevant page
      const handleNotificationTap = (notification: { data: Record<string, unknown> }) => {
        console.log('[SharePage] Notification tapped, data:', notification.data);
        
        const { type, postId, reactionId } = notification.data as {
          type?: string;
          postId?: string;
          reactionId?: string;
        };
        
        // Handle reaction notifications
        if (type === 'reaction' && postId) {
          const url = reactionId 
            ? `/my-pings/${postId}?reaction=${reactionId}`
            : `/my-pings/${postId}`;
          console.log('[SharePage] Navigating to:', url);
          navigate(url);
        }
        
        // Handle processing complete notifications
        if (type === 'processing-complete' && postId) {
          const url = `/my-pings/${postId}?processed=true`;
          console.log('[SharePage] Navigating to processed post:', url);
          navigate(url);
        }
      };
      
      initializePushNotifications(
        user.id,
        undefined, // onNotificationReceived - not needed for now
        handleNotificationTap
      ).then((result) => {
        if (result.success) {
          console.log('[SharePage] Push notifications initialized successfully');
        } else {
          console.log('[SharePage] Push notifications initialization failed:', result.error);
        }
      });
    }
  }, [isNative, user?.id, navigate]);

  // Track whether we've already processed a share intent this session
  const [hasProcessedShareIntent, setHasProcessedShareIntent] = useState(false);

  // Handle incoming shared media from Android share intent
  useEffect(() => {
    console.log('[SharePage] Share intent effect - sharedMedia:', sharedMedia ? 'present' : 'null', 'mediaFile:', mediaFile ? 'present' : 'null', 'hasProcessed:', hasProcessedShareIntent);
    if (sharedMedia && !mediaFile && !hasProcessedShareIntent) {
      console.log('[SharePage] Setting content type to:', sharedMedia.type, 'and file:', sharedMedia.file.name);
      // Set the content type based on the shared file type
      setContentType(sharedMedia.type);
      // Set the media file
      setMediaFile(sharedMedia.file);
      // Mark that this came from a share intent so we can close after upload
      setWasFromShareIntent(true);
      // Mark that we've processed this intent so we don't process it again
      setHasProcessedShareIntent(true);
    }
  }, [sharedMedia, mediaFile, hasProcessedShareIntent]);

  // Handle native sign-in
  const handleNativeSignIn = async () => {
    setSignInLoading(true);
    setSignInError(null);

    try {
      const result = await signInWithGoogleNative();
      
      // Authenticate with backend
      const res = await fetch(`${getApiBaseUrl()}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: result.idToken }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store tokens and reload to pick up auth state
      localStorage.setItem('ping_access_token', data.accessToken);
      localStorage.setItem('ping_refresh_token', data.refreshToken);
      localStorage.setItem('ping_user', JSON.stringify(data.user));
      
      // Reload page to pick up new auth state
      window.location.reload();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-in failed';
      // Don't show error for user cancellation
      if (!message.includes('cancelled') && !message.includes('canceled')) {
        setSignInError(message);
      }
    } finally {
      setSignInLoading(false);
    }
  };

  const isUploading = uploadState.state === 'uploading';
  const isDisabled = isUploading;

  const canSubmit = (() => {
    if (!contentType || isUploading) return false;
    switch (contentType) {
      case 'photo':
      case 'video':
        return !!mediaFile;
      case 'hudText':
        return !!hudText.trim();
      case 'voiceNote':
        return !!voiceBlob;
      default:
        return false;
    }
  })();

  const resetForm = () => {
    setContentType(null);
    setMediaFile(null);
    setVoiceBlob(null);
    setCaption('');
    setHudText('');
    setUploadState({ state: 'idle', progress: 0 });
    // Reset share intent tracking
    setWasFromShareIntent(false);
    // Also clear shared media so it doesn't get re-applied
    clearSharedMedia();
  };

  // Handler for when media file changes - clears shared media when file is removed
  const handleMediaFileChange = useCallback((file: File | null) => {
    setMediaFile(file);
    // If user is clearing the file, also clear the shared media
    // This prevents the share intent effect from re-populating the file
    if (file === null && sharedMedia) {
      clearSharedMedia();
      // Also clear the share intent flag since user manually cleared
      setWasFromShareIntent(false);
    }
    // If user manually picks a new file (not from share intent), clear the flag
    // This handles the case where user replaces shared content with their own
    if (file !== null && sharedMedia && file !== sharedMedia.file) {
      setWasFromShareIntent(false);
    }
  }, [sharedMedia, clearSharedMedia]);

  const handleSubmit = async () => {
    if (!canSubmit || !user) return;

    setUploadState({ state: 'uploading', progress: 0 });

    try {
      const formData = new FormData();
      formData.append('contentType', contentType!);
      
      // Add user identifier based on auth type
      const userIdent = getUserIdentifier();
      if (userIdent.userId) {
        formData.append('userId', userIdent.userId);
      }
      if (userIdent.userUuid) {
        formData.append('userUuid', userIdent.userUuid);
      }

      if (mediaFile) {
        formData.append('media', mediaFile);
      }
      if (voiceBlob) {
        const voiceFile = new File([voiceBlob], 'voice-note.webm', {
          type: voiceBlob.type,
        });
        formData.append('voiceNote', voiceFile);
      }
      if (caption.trim()) {
        formData.append('caption', caption.trim());
      }
      if (hudText.trim()) {
        formData.append('hudText', hudText.trim());
      }

      // Simulate progress while uploading
      const progressInterval = setInterval(() => {
        setUploadState((prev) => ({
          ...prev,
          progress: Math.min(prev.progress + 10, 90),
        }));
      }, 200);

      // Use appropriate endpoint and fetch method based on auth type
      const endpoint = user.authType === 'google' ? '/api/posts' : '/api/upload';
      
      // Use authenticatedFetch for Google auth (handles token refresh on 401)
      // Use regular fetch for local auth (no tokens involved)
      const response = user.authType === 'google'
        ? await authenticatedFetch(`${getApiBaseUrl()}${endpoint}`, {
            method: 'POST',
            body: formData,
          })
        : await fetch(`${getApiBaseUrl()}${endpoint}`, {
            method: 'POST',
            body: formData,
          });

      clearInterval(progressInterval);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const data = await response.json();
      
      // Check if this is a video that's still processing in the background
      if (data.processing) {
        // Video uploaded, but still processing - show different message
        setUploadState({ 
          state: 'processing', 
          progress: 100,
          postId: data.post?._id,
        });
        
        // If this was from a share intent, close the app after a brief delay
        if (wasFromShareIntent) {
          setTimeout(() => {
            finishShareIntent();
          }, 2500);
        } else {
          // Reset after showing processing message
          setTimeout(() => {
            resetForm();
          }, 3000);
        }
      } else {
        // Fully complete (photos, hudText, voiceNote)
        setUploadState({ state: 'success', progress: 100 });

        // If this was from a share intent, close the app after a brief delay
        if (wasFromShareIntent) {
          setTimeout(() => {
            finishShareIntent();
          }, 1500);
        } else {
          // Reset after success for normal app usage
          setTimeout(() => {
            resetForm();
          }, 2000);
        }
      }
    } catch (error) {
      setUploadState({
        state: 'error',
        progress: 0,
        error: error instanceof Error ? error.message : 'Upload failed',
      });
    }
  };

  const handleVoiceChange = useCallback((blob: Blob | null) => {
    setVoiceBlob(blob);
  }, []);

  // Show loading while checking auth or processing share intent
  if (authLoading || isProcessingShareIntent) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">
          {isProcessingShareIntent ? 'Loading shared file...' : 'Loading...'}
        </div>
      </div>
    );
  }

  // If on public server and not logged in, show sign-in prompt
  if (requiresGoogleAuth && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-background via-background to-primary/5">
        <div className="w-full max-w-sm space-y-8 animate-in">
          {/* Logo/Title */}
          <div className="text-center space-y-2">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-rose-500 to-fuchsia-600 flex items-center justify-center glow">
              <span className="text-4xl">📡</span>
            </div>
            <h1 className="text-3xl font-display font-bold gradient-text">
              Ping
            </h1>
            <p className="text-muted-foreground">
              Sign in required to share
            </p>
          </div>

          {/* Error Message */}
          {signInError && (
            <div className="flex items-center gap-2 p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{signInError}</p>
            </div>
          )}

          <div className="space-y-4">
            <p className="text-sm text-center text-muted-foreground">
              Only authorized accounts can access the app
            </p>
            
            {signInLoading ? (
              <Button size="xl" className="w-full" disabled>
                Signing in...
              </Button>
            ) : isNative ? (
              // Native sign-in button with Google branding
              <Button
                size="xl"
                onClick={handleNativeSignIn}
                className="w-full h-14 bg-white hover:bg-gray-100 text-gray-800 border border-gray-300"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Sign in with Google
              </Button>
            ) : (
              // Web: navigate to login page
              <Button
                size="xl"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                <LogIn className="w-5 h-5 mr-2" />
                Sign in with Google
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show name prompt if on internal server and not logged in
  if (!requiresGoogleAuth && !user) {
    return <NamePrompt onSubmit={loginLocal} />;
  }

  // Show success screen (for fully processed content like photos)
  if (uploadState.state === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-background via-background to-emerald-500/5">
        <div className="text-center space-y-6 animate-in">
          <div className="w-24 h-24 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-emerald-400">Sent!</h2>
            <p className="text-muted-foreground mt-1">
              Your ping is on the display
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show processing screen (for videos that are still being transcoded)
  if (uploadState.state === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-background via-background to-blue-500/5">
        <div className="text-center space-y-6 animate-in max-w-sm">
          <div className="w-24 h-24 mx-auto rounded-full bg-blue-500/20 flex items-center justify-center">
            <CheckCircle2 className="w-12 h-12 text-blue-500" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-blue-400">Upload Complete!</h2>
            <p className="text-muted-foreground">
              Your video is being processed in the background.
            </p>
            <p className="text-sm text-muted-foreground">
              You'll be notified when it's ready. You can close this app or continue using it.
            </p>
          </div>
          {!wasFromShareIntent && (
            <Button
              variant="outline"
              onClick={() => navigate('/my-pings')}
              className="mt-4"
            >
              <Inbox className="w-4 h-4 mr-2" />
              View My Pings
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Show error screen
  if (uploadState.state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6 animate-in">
          <div className="text-center space-y-4">
            <div className="w-20 h-20 mx-auto rounded-full bg-destructive/20 flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-destructive" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-destructive">
                Make screenshot and show to Ronny
              </h2>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-card border border-border">
            <p className="text-xs font-mono text-muted-foreground break-all">
              {uploadState.error}
            </p>
          </div>

          <Button onClick={resetForm} variant="outline" className="w-full">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Show uploading overlay
  if (isUploading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6 animate-in">
          <div className="text-center space-y-2">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center animate-pulse-slow">
              <Send className="w-8 h-8 text-primary" />
            </div>
            <p className="text-lg font-medium">Sending your ping...</p>
          </div>
          <Progress value={uploadState.progress} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 glass border-b border-border">
        <div className="max-w-lg mx-auto px-4 h-16 flex items-center justify-between relative">
          {contentType ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setContentType(null);
                // User is going back, clear any share intent state
                setWasFromShareIntent(false);
                clearSharedMedia();
              }}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-14 w-14 -ml-2">
                  <Menu className="w-7 h-7" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/my-pings')} className="py-4 text-base">
                  <Inbox className="w-5 h-5 mr-3" />
                  My Pings
                </DropdownMenuItem>
                {user?.authType === 'google' && 'role' in user && user.role === 'admin' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/admin')} className="py-4 text-base">
                      <Shield className="w-5 h-5 mr-3" />
                      Admin
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/stats')} className="py-4 text-base">
                      <BarChart3 className="w-5 h-5 mr-3" />
                      Stats
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <h1 className="absolute inset-0 flex items-center justify-center pointer-events-none font-display font-semibold gradient-text">Ping</h1>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                {user?.name}
                {user?.authType === 'google' && (
                  <span className="ml-1 text-xs text-primary">●</span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onClick={() => setShowLogoutConfirm(true)}
                className="text-destructive focus:text-destructive py-3 text-base"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-lg mx-auto p-4 pb-32 space-y-6">
        {/* Content Type Selection */}
        {!contentType && (
          <div className="space-y-4 animate-in">
            <h2 className="text-lg font-medium text-center">
              What do you want to share?
            </h2>
            <ContentTypeSelector
              selected={contentType}
              onSelect={(type) => {
                setContentType(type);
                // User is manually selecting content type, so this isn't from a share intent
                setWasFromShareIntent(false);
              }}
              disabled={isDisabled}
            />

            {/* Install App Button */}
            {appDownloadEnabled && (
              <div className="mt-8 pt-8 border-t border-border/50">
                <button
                  onClick={() => navigate('/download')}
                  className="w-full py-4 px-6 rounded-xl 
                    bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 
                    hover:from-violet-500 hover:via-fuchsia-500 hover:to-pink-500
                    text-white font-semibold text-lg
                    shadow-lg shadow-fuchsia-500/25 hover:shadow-fuchsia-500/40
                    transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]
                    flex items-center justify-center gap-3"
                >
                  <Download className="w-5 h-5" />
                  Install the App
                </button>
              </div>
            )}
          </div>
        )}

        {/* Photo Form */}
        {contentType === 'photo' && (
          <div className="space-y-6 animate-in">
            <MediaUploader
              type="photo"
              file={mediaFile}
              onFileChange={handleMediaFileChange}
              disabled={isDisabled}
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Caption (optional)
              </label>
              <Textarea
                placeholder="Add a caption..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                disabled={isDisabled}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Voice note (optional)
              </label>
              <VoiceRecorder
                onRecordingChange={handleVoiceChange}
                disabled={isDisabled}
              />
            </div>
          </div>
        )}

        {/* Video Form */}
        {contentType === 'video' && (
          <div className="space-y-6 animate-in">
            <MediaUploader
              type="video"
              file={mediaFile}
              onFileChange={handleMediaFileChange}
              disabled={isDisabled}
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Description (optional)
              </label>
              <Textarea
                placeholder="Add a description..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                disabled={isDisabled}
                rows={3}
              />
            </div>
          </div>
        )}

        {/* HUD Text Form */}
        {contentType === 'hudText' && (
          <div className="space-y-6 animate-in">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Message to display
              </label>
              <Textarea
                placeholder="Type your message..."
                value={hudText}
                onChange={(e) => setHudText(e.target.value)}
                disabled={isDisabled}
                rows={4}
                className="text-lg"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Voice note (optional)
              </label>
              <VoiceRecorder
                onRecordingChange={handleVoiceChange}
                disabled={isDisabled}
              />
            </div>
          </div>
        )}

        {/* Voice Note Form */}
        {contentType === 'voiceNote' && (
          <div className="space-y-6 animate-in">
            <div className="text-center space-y-2 mb-8">
              <h2 className="text-lg font-medium">Record a voice message</h2>
              <p className="text-sm text-muted-foreground">
                This will play on the speakers
              </p>
            </div>

            <VoiceRecorder
              onRecordingChange={handleVoiceChange}
              disabled={isDisabled}
            />
          </div>
        )}
      </main>

      {/* Submit Button */}
      {contentType && (
        <div className="fixed bottom-0 left-0 right-0 p-4 glass border-t border-border">
          <div className="max-w-lg mx-auto">
            <Button
              size="xl"
              className={cn(
                'w-full',
                !canSubmit && 'opacity-50'
              )}
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              <Send className="w-5 h-5" />
              Send Ping
            </Button>
          </div>
        </div>
      )}

      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Logout</DialogTitle>
            <DialogDescription>
              Are you sure you want to logout?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={async () => {
                if (isNative) {
                  await unregisterPushToken();
                }
                logout();
                window.location.reload();
              }}
            >
              Logout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
